import { useCallback, useState } from "react";

interface History<T> {
  past: T[];
  present: T;
  future: T[];
}

export function useHistory<T>(initial: T) {
  const [history, setHistory] = useState<History<T>>({ past: [], present: initial, future: [] });

  const commit = useCallback((next: T | ((current: T) => T)) => {
    setHistory((current) => ({
      past: [...current.past.slice(-49), current.present],
      present: typeof next === "function" ? (next as (value: T) => T)(current.present) : next,
      future: [],
    }));
  }, []);

  const replace = useCallback((next: T) => setHistory((current) => ({ ...current, present: next })), []);
  const reset = useCallback((next: T) => setHistory({ past: [], present: next, future: [] }), []);
  const undo = useCallback(() => setHistory((current) => current.past.length ? {
    past: current.past.slice(0, -1),
    present: current.past[current.past.length - 1],
    future: [current.present, ...current.future],
  } : current), []);
  const redo = useCallback(() => setHistory((current) => current.future.length ? {
    past: [...current.past, current.present],
    present: current.future[0],
    future: current.future.slice(1),
  } : current), []);

  return { value: history.present, commit, replace, reset, undo, redo, canUndo: history.past.length > 0, canRedo: history.future.length > 0 };
}
